import { useEffect, type CSSProperties, type ReactNode } from "react";
import {
  createFileRoute,
  Navigate,
  Outlet,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProject } from "@/hooks/use-project";
import { usePermissions, useCanOnResource } from "@/hooks/use-permissions";
import { useAutoCacheOnOpen } from "@/hooks/use-auto-cache-on-open";
import { ProjectHeader } from "@/components/common/project-header/project-header";
import { ProjectHeaderRecordsAction } from "@/components/common/project-header/project-header-records-action";
import { ProjectHeaderIssuesAction } from "@/components/common/project-header/project-header-issues-action";
import { PlansViewModeProvider } from "@/components/plans/plans-view/plans-view-mode";
import { RecordsViewModeProvider } from "@/components/records/records-view/records-view-mode";
import { BottomNav } from "@/components/common/bottom-nav/bottom-nav";
import {
  aboveBottomStrip,
  BOTTOM_STRIP_VAR,
  NAV_HEIGHT_PX,
  RAIL_WIDTH_PX,
  RAIL_WIDTH_VAR,
} from "@/components/common/bottom-nav/nav-constants";
import { useBottomNavHidden } from "@/components/common/bottom-nav/use-bottom-nav-hidden";
import { InstallAppBanner } from "@/components/common/install-app-banner/install-app-banner";
import { OfflineDebugOverlay } from "@/components/common/offline-debug-overlay/offline-debug-overlay";
import {
  SelectionModeProvider,
  useSelectionMode,
} from "@/components/records/selection-mode/selection-mode-provider";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { Button } from "@/components/ui/button";
import { permittedTabs, projectCan, tabForPath } from "@/lib/project-tabs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/projects/$projectId")({
  component: ProjectLayout,
});

/**
 * The frame while the project loads, or fails to: the navigation isn't drawn
 * yet, but its space is kept so the page doesn't jump when it arrives — the
 * rail's width from 768px, the bottom bar's height on a phone.
 */
function PlaceholderShell({
  children,
  mainClassName,
}: {
  children?: ReactNode;
  mainClassName?: string;
}) {
  const isDesktop = useIsDesktop();
  const shellVars = {
    [BOTTOM_STRIP_VAR]: isDesktop ? "0px" : `${NAV_HEIGHT_PX}px`,
    [RAIL_WIDTH_VAR]: isDesktop ? `${RAIL_WIDTH_PX}px` : "0px",
  } as CSSProperties;
  return (
    <div className="flex h-app-screen flex-col" style={shellVars}>
      <ProjectHeader projectName={undefined} />
      <div className="flex min-h-0 flex-1">
        {isDesktop && (
          <div
            aria-hidden="true"
            className="shrink-0 border-r border-border bg-background"
            style={{ width: RAIL_WIDTH_PX }}
          />
        )}
        <main
          className={cn("min-w-0 flex-1", mainClassName)}
          style={{ paddingBottom: aboveBottomStrip(0) }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

function LoadingShell() {
  return <PlaceholderShell />;
}

function ErrorShell({ onRetry }: { onRetry: () => void }) {
  return (
    <PlaceholderShell mainClassName="flex flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-sm text-muted-foreground">
        Couldn't load this project — check your connection and try again.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </PlaceholderShell>
  );
}

/**
 * What runs along the bottom of the shell, in px: the phone's nav or the bulk
 * action bar (72), or nothing (0). See [[safe-area-bottom]] § The bottom strip.
 */
function bottomStripPx({
  navHidden,
  drawsTabs,
  isDesktop,
  isSelectionMode,
}: {
  navHidden: boolean;
  drawsTabs: boolean;
  isDesktop: boolean;
  isSelectionMode: boolean;
}): number {
  // A phone's nested screen draws no nav, and so no bulk bar either.
  if (navHidden) return 0;
  // The bulk action bar takes the bottom at every width.
  if (isSelectionMode) return NAV_HEIGHT_PX;
  // From 768px the nav is the rail on the left; with one tab, there is none.
  return isDesktop || !drawsTabs ? 0 : NAV_HEIGHT_PX;
}

/**
 * The frame every loaded project page renders in: the header across the top,
 * then the navigation beside the scrollable page. From 768px the nav is a rail
 * that takes its place in the row, so the page starts at its edge; on a phone
 * it is fixed to the bottom and the row holds only the page.
 *
 * Decides, once, what runs along the bottom and how wide the rail is, and
 * publishes both as CSS custom properties, so `<main>`'s padding, the FABs and
 * the bulk action bar all read the same numbers ([[safe-area-bottom]]).
 */
function ProjectShell({
  header,
  nav,
  navHidden,
  drawsTabs,
  children,
  overlay,
}: {
  header: ReactNode;
  nav: ReactNode;
  navHidden: boolean;
  /** More than one permitted tab: the bar or rail is drawn and takes room. */
  drawsTabs: boolean;
  children: ReactNode;
  overlay: ReactNode;
}) {
  const isDesktop = useIsDesktop();
  const { isSelectionMode } = useSelectionMode();
  const strip = bottomStripPx({
    navHidden,
    drawsTabs,
    isDesktop,
    isSelectionMode,
  });
  const railWidth = isDesktop && !navHidden && drawsTabs ? RAIL_WIDTH_PX : 0;
  const shellVars = {
    [BOTTOM_STRIP_VAR]: `${strip}px`,
    [RAIL_WIDTH_VAR]: `${railWidth}px`,
  } as CSSProperties;

  return (
    <div className="flex h-app-screen flex-col" style={shellVars}>
      {header}
      <div className="flex min-h-0 flex-1">
        {/* First in the row at every width: the rail draws here from 768px,
            and the phone's fixed bar takes no room in it. One position means
            crossing the breakpoint never remounts the nav and its capture
            flow. */}
        {nav}
        <main
          className="min-w-0 flex-1 overflow-auto"
          style={{ paddingBottom: aboveBottomStrip(0) }}
        >
          {children}
        </main>
      </div>
      {overlay}
      {/* Inside the element that publishes the strip, so it inherits it. Not on
          a phone's nested screens: no nav there, and the issue page's own
          bottom bar is not published (FE-DEBT-053). See
          [[install-app-banner.spec]] § Where it shows. */}
      {!navHidden && <InstallAppBanner aboveFabs />}
    </div>
  );
}

function MainErrorFallback({
  resetErrorBoundary,
}: {
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-sm text-destructive">
        This area failed to load.
      </p>
      <Button variant="outline" size="sm" onClick={resetErrorBoundary}>
        Try again
      </Button>
    </div>
  );
}

export function ProjectLayout() {
  const { projectId } = useParams({ from: "/projects/$projectId" });
  const { data: project, isLoading, error, refetch } = useProject(projectId);
  const { data: currentUser } = useCurrentUser();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const permissionsProject = project
    ? { owner: project.owner.id, my_roles: project.my_roles }
    : { owner: "", my_roles: [] };
  const { can } = usePermissions(permissionsProject);
  const canOnResource = useCanOnResource(permissionsProject);
  const { reset } = useQueryErrorResetBoundary();
  // One report or one issue, on a phone: no tab bar, so the screen reads as a
  // level below its tab. See [[bottom-nav.spec]] § Nested screens. The shell
  // reads the same flag for the space it keeps at the bottom.
  const bottomNavHidden = useBottomNavHidden();

  // Auto-cache this project for offline once it's open and loaded — silent, in
  // the background, after a short dwell. Gated to users who can view records (a
  // project with no viewable records has nothing useful to cache); `enabled` is
  // false while loading/errored so the hook no-ops until there's a project.
  // See specs/technical/auto-cache-on-open.spec.md.
  useAutoCacheOnOpen({
    projectId,
    canViewRecords: can("record:View:Self") || can("record:View:Other"),
    enabled: !!project,
  });

  // Report the no-cache-no-network case to Sentry once per error reference.
  // Calling captureException in the render body would fire on every re-render
  // (StrictMode double-render, parent updates, etc.) and TanStack mints a new
  // Error per failed refetch, so Sentry's dedupe wouldn't help.
  const shouldReport = !isLoading && !project && error != null;
  useEffect(() => {
    if (shouldReport && error) Sentry.captureException(error);
  }, [shouldReport, error]);

  if (isLoading) {
    return <LoadingShell />;
  }

  if (!project) {
    return <ErrorShell onRetry={() => refetch()} />;
  }

  // The tabs this user may use. Read with the owner presumed while the profile
  // loads, so an owner's navigation doesn't flicker in late. See
  // [[bottom-nav.spec]] § Tabs follow permissions.
  const tabs = permittedTabs(projectCan(permissionsProject, currentUser?.id));

  // A tab's page without its permission — an old link, a bookmark, Back —
  // goes to the first one they may use, replacing the entry. Only once the
  // user is known: a presumption is good enough for drawing tabs, not for
  // moving someone off the page they asked for.
  const currentTab = tabForPath(pathname, projectId);
  if (currentUser && currentTab && tabs.length > 0 && !tabs.includes(currentTab)) {
    return <Navigate to={tabs[0].to} params={{ projectId }} replace />;
  }

  // SelectionModeProvider lives at the layout level so both the records
  // view (state owner) and the bottom-nav action bar (state consumer) can
  // share the same selection mode without prop-drilling. Per
  // [[selection-mode.spec]] § Provider architecture.
  return (
    <SelectionModeProvider>
      <PlansViewModeProvider>
        <RecordsViewModeProvider>
          <ProjectShell
            navHidden={bottomNavHidden}
            drawsTabs={tabs.length > 1}
            header={
              <ProjectHeader
                projectName={project.name}
                rightAction={
                  <>
                    <ProjectHeaderRecordsAction />
                    <ProjectHeaderIssuesAction
                      canCreateReport={can("report:Create")}
                    />
                  </>
                }
                projectId={projectId}
                recordsCount={project.records_count}
                maxRecordCount={project.max_record_count}
                memberCount={project.member_count}
                ownerId={project.owner.id}
                myRoles={project.my_roles}
                can={can}
              />
            }
            nav={
              !bottomNavHidden && (
                <BottomNav
                  projectId={projectId}
                  tabs={tabs}
                  can={can}
                  canOnResource={canOnResource}
                  metadataFields={project.metadata_fields}
                  projectTags={project.tags}
                  projectPlans={project.plans}
                  recordsCount={project.records_count}
                  maxRecordCount={project.max_record_count}
                />
              )
            }
            overlay={<OfflineDebugOverlay projectId={projectId} />}
          >
            {/* Localized boundary: a thrown error inside the records / plans /
                reports / settings outlet stays scoped to the main area so the
                header + navigation remain interactive. Without this, a sibling
                query that throws would escape to the root QueryErrorBoundary
                and nuke the entire project shell. */}
            <ErrorBoundary
              onReset={reset}
              onError={(err) => Sentry.captureException(err)}
              fallbackRender={({ resetErrorBoundary }) => (
                <MainErrorFallback resetErrorBoundary={resetErrorBoundary} />
              )}
            >
              <Outlet />
            </ErrorBoundary>
          </ProjectShell>
        </RecordsViewModeProvider>
      </PlansViewModeProvider>
    </SelectionModeProvider>
  );
}
